import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { AgentState } from "../store/agentSlice";
import { toCaretColour, type CaretColour } from "../theme";

type State = { agent: AgentState };

/** Another tab's caret and selection on this thread, as it last reported them (unclamped). */
export interface RemoteCaret {
  tabId: string;
  colour: CaretColour;
  caret: unknown;
  selection: unknown;
}

export interface TabCarets {
  /** Markers show only when the thread is open in more than one tab, as the dots do. */
  show: boolean;
  own: CaretColour;
  /** Idle tabs are left out until they report input again. */
  others: RemoteCaret[];
}

const NONE: TabCarets = { show: false, own: 0, others: [] };

const makeSelectTabCarets = () =>
  createSelector(
    [
      (state: State) => state.agent.presence,
      (state: State) => state.agent.connection.tabId,
      (state: State) => state.agent.connection.colour,
      (_state: State, threadId: number | null) => threadId,
    ],
    (tabs, thisTab, colour, threadId): TabCarets => {
      const here = threadId == null ? [] : tabs.filter((tab) => tab.thread_id === threadId);
      if (here.length < 2) return NONE;
      return {
        show: true,
        own: toCaretColour(here.find((tab) => tab.tab_id === thisTab)?.colour ?? colour),
        others: here
          .filter((tab) => tab.tab_id !== thisTab && !tab.idle)
          .map((tab) => ({
            tabId: tab.tab_id,
            colour: toCaretColour(tab.colour),
            caret: tab.caret,
            selection: tab.selection,
          })),
      };
    }
  );

const sameRange = (a: unknown, b: unknown) =>
  a === b ||
  (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]));

/** Every presence frame is a new list: re-render only when this thread's markers changed. */
function sameCarets(a: TabCarets, b: TabCarets): boolean {
  return (
    a.show === b.show &&
    a.own === b.own &&
    a.others.length === b.others.length &&
    a.others.every((tab, i) => {
      const other = b.others[i];
      return (
        tab.tabId === other.tabId &&
        tab.colour === other.colour &&
        tab.caret === other.caret &&
        sameRange(tab.selection, other.selection)
      );
    })
  );
}

export function useTabCarets(threadId: number | null): TabCarets {
  const select = useMemo(makeSelectTabCarets, []);
  return useSelector((state: State) => select(state, threadId), sameCarets);
}
